import React from 'react';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import { Shield, Lock, Eye, Database, Users, Bell, Globe, FileText } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
              <Shield className="text-blue-500" size={32} />
              Privacy Policy
            </h1>
            <p className="text-sm text-text-muted mt-1">Last Updated: August 24, 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl">
        <Card className="prose prose-slate dark:prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-8">
            <p className="text-lg text-text-secondary leading-relaxed">
              SecureView ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our security camera management platform and mobile application.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Database className="text-blue-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">Information We Collect</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-text-primary mt-6 mb-3">Personal Information</h3>
            <p className="text-text-secondary leading-relaxed">
              When you register for SecureView, we may collect:
            </p>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Account Information:</strong> Name, email address, phone number, company name</li>
              <li><strong>Authentication Credentials:</strong> Username, encrypted password</li>
              <li><strong>Profile Data:</strong> User preferences, role, and permissions</li>
              <li><strong>Payment Information:</strong> Billing address and payment card details (processed securely by third-party payment processors)</li>
            </ul>

            <h3 className="text-xl font-semibold text-text-primary mt-6 mb-3">Camera and Security Data</h3>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Video and Images:</strong> Camera feeds, recorded footage, snapshots, and event captures</li>
              <li><strong>AI-Generated Metadata:</strong> Object detection, license plate recognition, motion analysis, and scene descriptions</li>
              <li><strong>Event Logs:</strong> Motion detection events, intrusion alerts, line crossing notifications</li>
              <li><strong>Location Data:</strong> Camera locations, facility addresses, and geographic coordinates</li>
            </ul>

            <h3 className="text-xl font-semibold text-text-primary mt-6 mb-3">Visitor and Access Logs</h3>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Visitor Information:</strong> Names, company affiliations, vehicle details, license plates</li>
              <li><strong>Entry/Exit Records:</strong> Timestamps, access points, visitor purpose</li>
              <li><strong>ID Photos:</strong> Driver's license images, face captures (when provided)</li>
            </ul>

            <h3 className="text-xl font-semibold text-text-primary mt-6 mb-3">Technical Information</h3>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages viewed, features used, time spent, click patterns</li>
              <li><strong>Log Data:</strong> Access times, error logs, API requests</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="text-purple-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">How We Use Your Information</h2>
            </div>
            <p className="text-text-secondary leading-relaxed">We use the collected information for:</p>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Service Delivery:</strong> Provide camera management, live streaming, playback, and event monitoring</li>
              <li><strong>AI Processing:</strong> Analyze video footage for object detection, license plate recognition, and security insights</li>
              <li><strong>Security Operations:</strong> Monitor facilities, detect intrusions, track visitor access</li>
              <li><strong>Account Management:</strong> Authenticate users, manage permissions, provide customer support</li>
              <li><strong>Notifications:</strong> Send alerts for detected events, system updates, security warnings</li>
              <li><strong>Analytics:</strong> Improve service performance, analyze usage patterns, optimize features</li>
              <li><strong>Compliance:</strong> Meet legal obligations, respond to law enforcement requests</li>
            </ul>
          </section>

          {/* Data Storage and Security */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="text-green-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">Data Storage and Security</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-text-primary mt-6 mb-3">Storage Infrastructure</h3>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Cloud Storage:</strong> Video footage and images stored in Amazon S3 (US East region)</li>
              <li><strong>Database:</strong> User and event data stored in encrypted MySQL databases</li>
              <li><strong>Local Storage:</strong> Optional on-premises NVR integration for hybrid deployments</li>
              <li><strong>Retention:</strong> Media files retained for 30-90 days (configurable per location)</li>
            </ul>

            <h3 className="text-xl font-semibold text-text-primary mt-6 mb-3">Security Measures</h3>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Encryption:</strong> TLS/SSL for data in transit, AES-256 for data at rest</li>
              <li><strong>Access Control:</strong> Role-based permissions, multi-factor authentication</li>
              <li><strong>Authentication:</strong> JWT tokens with expiration, secure session management</li>
              <li><strong>Monitoring:</strong> 24/7 security monitoring, intrusion detection, audit logs</li>
              <li><strong>Backups:</strong> Regular automated backups with disaster recovery procedures</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-orange-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">Information Sharing and Disclosure</h2>
            </div>
            <p className="text-text-secondary leading-relaxed">We may share your information with:</p>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Service Providers:</strong> AWS (cloud storage), HikVision (camera systems), payment processors</li>
              <li><strong>AI Services:</strong> Amazon Bedrock for video analysis (data processed securely)</li>
              <li><strong>Business Partners:</strong> Integration partners with your explicit consent</li>
              <li><strong>Legal Authorities:</strong> When required by law, court order, or legal process</li>
              <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-4">
              <strong>We do NOT:</strong> Sell your personal information, share camera footage publicly, or use your data for unrelated marketing purposes.
            </p>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="text-cyan-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">Third-Party Services</h2>
            </div>
            <p className="text-text-secondary leading-relaxed">SecureView integrates with:</p>
            <ul className="text-text-secondary space-y-2">
              <li><strong>HikVision/HikCentral:</strong> Camera firmware, cloud services (governed by HikVision's privacy policy)</li>
              <li><strong>Amazon Web Services:</strong> S3 storage, Bedrock AI (governed by AWS privacy policy)</li>
              <li><strong>Payment Processors:</strong> Stripe or similar (PCI-DSS compliant)</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-4">
              These third parties have their own privacy policies. We recommend reviewing them.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="text-indigo-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">Your Privacy Rights</h2>
            </div>
            <p className="text-text-secondary leading-relaxed">Depending on your location, you may have rights to:</p>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Data Portability:</strong> Export your data in a structured format</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails or notifications</li>
              <li><strong>Restrict Processing:</strong> Limit how we use your data</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@secureview.com" className="text-blue-500 hover:underline">privacy@secureview.com</a>
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="text-yellow-500" size={24} />
              <h2 className="text-2xl font-bold text-text-primary m-0">Data Retention</h2>
            </div>
            <ul className="text-text-secondary space-y-2">
              <li><strong>Video Footage:</strong> Retained for 30-90 days unless flagged for longer storage</li>
              <li><strong>Visitor Logs:</strong> Retained for 1 year or as required by local regulations</li>
              <li><strong>Event Data:</strong> Retained for 2 years for security analysis</li>
              <li><strong>Account Information:</strong> Retained while your account is active, deleted within 30 days of account closure</li>
              <li><strong>Audit Logs:</strong> Retained for 7 years for compliance purposes</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-text-primary mb-3">Children's Privacy</h2>
            <p className="text-text-secondary leading-relaxed">
              SecureView is a business application intended for users 18 years and older. We do not knowingly collect information from children under 13. If you become aware that a child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-text-primary mb-3">Changes to This Privacy Policy</h2>
            <p className="text-text-secondary leading-relaxed">
              We may update this Privacy Policy periodically. We will notify you of significant changes via email or through the application. Continued use of SecureView after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-text-primary mb-3">Contact Us</h2>
            <p className="text-text-secondary leading-relaxed">
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="bg-surface-secondary p-4 rounded-lg mt-4">
              <p className="text-text-primary font-semibold mb-2">SecureView Privacy Team</p>
              <p className="text-text-secondary">Email: <a href="mailto:privacy@secureview.com" className="text-blue-500 hover:underline">privacy@secureview.com</a></p>
              <p className="text-text-secondary">Website: <a href="https://secureview.com" className="text-blue-500 hover:underline">https://secureview.com</a></p>
              <p className="text-text-secondary">Phone: 1-800-SECUREVIEW</p>
            </div>
          </section>

          {/* Compliance */}
          <section className="mb-4">
            <h2 className="text-2xl font-bold text-text-primary mb-3">Regulatory Compliance</h2>
            <p className="text-text-secondary leading-relaxed">
              SecureView complies with applicable privacy laws including:
            </p>
            <ul className="text-text-secondary space-y-1">
              <li>• Personal Information Protection and Electronic Documents Act (PIPEDA) - Canada</li>
              <li>• General Data Protection Regulation (GDPR) - European Union</li>
              <li>• California Consumer Privacy Act (CCPA) - California, USA</li>
              <li>• State-specific privacy laws where applicable</li>
            </ul>
          </section>
        </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default PrivacyPolicyPage;
